import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Responsible } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { ResponsibleForm } from "../ResponsibleForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListResponsibles.scss";
import { useLanguage } from "../../../../contexts";

const responsibleController = new Responsible();

export function ListResponsibles({ responsiblesActive, reload, onReload }) {
  const { accessToken } = useAuth();
  const [responsibles,       setResponsibles]       = useState(null);
  const [responsiblesFilter, setResponsiblesFilter] = useState([]);
  const [search,             setSearch]             = useState("");
  const [showModal,          setShowModal]          = useState(false);
  const [selected,           setSelected]           = useState(null);
  const [showConfirm,        setShowConfirm]        = useState(false);
  const [confirmMsg,         setConfirmMsg]         = useState("");
  const [isDelete,           setIsDelete]           = useState(false);
  const [rowMenu,            setRowMenu]            = useState(null); // { id, top, left, openUp } | null

  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".responsibles-list__row-menu, .responsibles-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setResponsibles(null);
        const res = await responsibleController.getResponsibles(accessToken, responsiblesActive);
        setResponsibles(res);
        setResponsiblesFilter(res);
      } catch {
        setResponsibles([]);
        setResponsiblesFilter([]);
      }
    })();
  }, [responsiblesActive, reload]);

  useEffect(() => {
    if (!responsibles) return;
    if (!search) { setResponsiblesFilter(responsibles); return; }
    const q = search.toLowerCase();
    setResponsiblesFilter(responsibles.filter((d) =>
      d.position?.toLowerCase().includes(q) ||
      d.country?.name?.toLowerCase().includes(q) ||
      d.country?.code?.toLowerCase().includes(q) ||
      d.industry?.name?.toLowerCase().includes(q) ||
      d.industry?.code?.toLowerCase().includes(q) ||
      d.division?.name?.toLowerCase().includes(q) ||
      d.division?.code?.toLowerCase().includes(q)
    ));
  }, [search, responsibles]);

  const openCreate = () => { setSelected(null); setShowModal(true); };
  const openEdit   = (d)  => { setSelected(d);   setShowModal(true); };

  const openToggle = (d) => {
    setSelected(d); setIsDelete(false);
    setConfirmMsg(`¿${d.active ? t("deactivate") : t("activate")} "${d.position}"?`);
    setShowConfirm(true);
  };

  const openDelete = (d) => {
    setSelected(d); setIsDelete(true);
    setConfirmMsg(`${t("are_you_sure_you_want_to_remove")} "${d.position}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await responsibleController.updateResponsible(accessToken, selected._id, { active: !selected.active });
      toast.success(selected.active ? t("deactivated") : t("activated"));
      setShowConfirm(false); onReload();
    } catch { toast.error(t("error")); }
  };

  const handleDelete = async () => {
    try {
      await responsibleController.deleteResponsible(accessToken, selected._id);
      toast.success(t("deleted")); setShowConfirm(false); onReload();
    } catch (err) { toast.error(err?.msg || t("error")); }
  };

  if (responsibles === null) return <LoadingSpinner />;

  return (
    <div className="responsibles-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="responsibles-list__card">
        <div className="responsibles-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input className="search-user-input" placeholder={t("search")} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Button icon className="add-responsible-btn" title={t("create")} onClick={openCreate}><Icon name="plus" /></Button>
        </div>

        {responsiblesFilter.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="responsibles-list__tbl-scroll">
            <Table className="responsibles-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>País</Table.HeaderCell>
                  <Table.HeaderCell>Industria</Table.HeaderCell>
                  <Table.HeaderCell>División</Table.HeaderCell>
                  <Table.HeaderCell>Puesto</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {responsiblesFilter.map((d) => (
                  <Table.Row key={d._id}>
                    <Table.Cell data-label="País">{d.country?.code || d.country?.name || "—"}</Table.Cell>
                    <Table.Cell data-label="Industria">{d.industry?.code || d.industry?.name || "—"}</Table.Cell>
                    <Table.Cell data-label="División">{d.division?.code || d.division?.name || "—"}</Table.Cell>
                    <Table.Cell data-label="Puesto">{d.position}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="responsibles-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="responsibles-list__row-menu">
                        <button type="button" className="responsibles-list__row-menu-btn" onClick={(e) => toggleRowMenu(d._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === d._id && ReactDOM.createPortal(
                        <div
                          className="responsibles-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(d); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(d); setRowMenu(null); }}>
                            <Icon name={d.active ? "ban" : "check"} />{d.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="responsibles-list__row-menu-item--danger" onClick={() => { openDelete(d); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal show={showModal} close={() => setShowModal(false)} title={selected ? `${t("edit")}: ${selected.position}` : t("new_responsible")} size="small" className="catalog-modal">
        <ResponsibleForm responsible={selected} close={() => setShowModal(false)} onReload={onReload} />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selected?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
